import type { ScoringRules, UserAnswer, Question } from '@examflow/core';
import { TOPIKLevel, TOPIKSection, type TOPIKScoreMapping, type TOPIKResult } from './types';
import { getLevelDescription } from './levels';

/**
 * TOPIK 分数与等级对照表
 * TOPIK I: 200分 (1-2级) | TOPIK II: 300分 (3-6级)
 * 此处使用 TOPIK II 总分 300 作为参考
 */
export const TOPIK_SCORE_MAPPINGS: TOPIKScoreMapping[] = [
  {
    level: TOPIKLevel.Level1,
    minScore: 80,
    maxScore: 139,
    description: '初级1级 - 能够完成基本的日常交流',
  },
  {
    level: TOPIKLevel.Level2,
    minScore: 140,
    maxScore: 199,
    description: '初级2级 - 能够完成日常生活中的基本交流',
  },
  {
    level: TOPIKLevel.Level3,
    minScore: 120,
    maxScore: 149,
    description: '中级3级 - 能够处理日常生活中的交流',
  },
  {
    level: TOPIKLevel.Level4,
    minScore: 150,
    maxScore: 189,
    description: '中级4级 - 能够处理公共设施中的交流',
  },
  {
    level: TOPIKLevel.Level5,
    minScore: 190,
    maxScore: 229,
    description: '高级5级 - 能够处理专业领域的研究或业务',
  },
  {
    level: TOPIKLevel.Level6,
    minScore: 230,
    maxScore: 300,
    description: '高级6级 - 能够熟练地处理专业领域的研究或业务',
  },
];

/**
 * 根据分数获取 TOPIK 等级
 * 使用 TOPIK II 总分 300 标准: 3级 120-149, 4级 150-189, 5级 190-229, 6级 230-300
 * 使用 TOPIK I 标准: 1级 80-139, 2级 140-200 (分数 80-199 时)
 */
export function getTOPIKLevel(score: number): TOPIKLevel {
  if (score >= 230) return TOPIKLevel.Level6;
  if (score >= 190) return TOPIKLevel.Level5;
  if (score >= 150) return TOPIKLevel.Level4;
  if (score >= 120) return TOPIKLevel.Level3;
  if (score >= 140) return TOPIKLevel.Level2;
  if (score >= 80) return TOPIKLevel.Level1;
  return TOPIKLevel.Level1;
}

/**
 * 根据分数判断是否及格
 * TOPIK I: 80+ | TOPIK II: 120+
 */
export function isTOPIKPassed(score: number): boolean {
  return score >= 80;
}

/**
 * 获取等级详细信息
 */
export function getLevelInfo(level: TOPIKLevel): TOPIKScoreMapping | undefined {
  return TOPIK_SCORE_MAPPINGS.find((m) => m.level === level);
}

/**
 * TOPIK 评分规则
 * 总分 300（TOPIK II），及格分 80（TOPIK I 1级）
 */
export const topikScoringRules: ScoringRules = {
  totalScore: 300,
  passingScore: 80,
  calculateScore: (answers: UserAnswer[], questions: Question[]): number => {
    if (questions.length === 0) return 0;

    const correctCount = answers.filter((a) => a.isCorrect).length;
    const accuracy = correctCount / questions.length;

    const score = Math.round(accuracy * 300);

    return Math.min(300, Math.max(0, score));
  },
};

/**
 * 计算完整的 TOPIK 结果
 */
export function calculateTOPIKResult(
  answers: UserAnswer[],
  questions: Question[]
): TOPIKResult {
  const score = topikScoringRules.calculateScore(answers, questions);
  const level = getTOPIKLevel(score);
  const passed = isTOPIKPassed(score);
  const levelDesc = getLevelDescription(level);

  const sectionScores = {
    [TOPIKSection.LISTENING]: 0,
    [TOPIKSection.READING]: 0,
    [TOPIKSection.WRITING]: 0,
  };

  return {
    totalScore: score,
    level,
    sectionScores,
    passed,
    evaluation: levelDesc?.description ?? '继续努力！',
  };
}
